import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useResolvedPath,
  type To,
} from 'react-router-dom';
import {
  RouteNoticeContext,
  useRouteNotice,
  type RouteNoticeContextValue,
  type RouteNoticeInput,
  type RouteNoticeTone,
} from './routeNoticeContext';

interface ActiveRouteNotice extends RouteNoticeInput {
  arrivalKey: string | null;
}

function routeUrl(location: { pathname: string; search?: string; hash?: string }): string {
  return `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
}

export function RouteNoticeProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [notice, setNotice] = useState<ActiveRouteNotice | null>(null);
  const currentUrl = routeUrl(location);

  const announce = useCallback((input: RouteNoticeInput) => {
    setNotice({
      ...input,
      arrivalKey: null,
    });
  }, []);

  useEffect(() => {
    setNotice((current) => {
      if (!current) return current;

      if (current.arrivalKey === null) {
        if (currentUrl === current.destination) {
          return { ...current, arrivalKey: location.key };
        }

        // The announcing component may render once before its redirect effect is
        // processed. Any other location means the redirect did not reach the
        // declared destination, so do not leave a hidden notice waiting around.
        return location.key === current.originKey ? current : null;
      }

      // A notice belongs to the exact history entry on which it arrived. The next
      // navigation (including a same-URL navigation with a new key) consumes it.
      return location.key === current.arrivalKey ? current : null;
    });
  }, [currentUrl, location.key]);

  const value = useMemo<RouteNoticeContextValue>(() => ({ announce }), [announce]);
  const visibleNotice = notice?.arrivalKey === location.key ? notice : null;

  return (
    <RouteNoticeContext.Provider value={value}>
      {visibleNotice && (
        <div
          className={`sp-route-notice sp-route-notice--${visibleNotice.tone}`}
          role="status"
          aria-live="polite"
        >
          <span>{visibleNotice.message}</span>
          <button
            type="button"
            className="sp-route-notice-dismiss"
            aria-label="Dismiss notice"
            onClick={() => setNotice(null)}
          >
            ×
          </button>
        </div>
      )}
      {children}
    </RouteNoticeContext.Provider>
  );
}

export function NoticeRedirect({
  to,
  message,
  tone = 'warning',
  replace = true,
}: {
  to: To;
  message: string;
  tone?: RouteNoticeTone;
  replace?: boolean;
}) {
  const { announce } = useRouteNotice();
  const location = useLocation();
  const navigate = useNavigate();
  const resolvedDestination = useResolvedPath(to);
  const destination = routeUrl(resolvedDestination);
  const redirected = useRef(false);

  useEffect(() => {
    if (redirected.current) return;
    redirected.current = true;
    announce({
      message,
      tone,
      destination,
      originKey: location.key,
    });
    navigate(to, { replace });
  }, [announce, destination, location.key, message, navigate, replace, to, tone]);

  return null;
}
